import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { Badge } from "react-bootstrap";
import TreeNavigator, { type TreeNode } from "@components/TreeNavigator/TreeNavigator";
import { Job } from "@models/job.model";
import { Paths } from "../../../paths";

function getStatusColor(status: string) {
    const s = status?.toLowerCase?.() || "";
    if (s === "failed" || s === "error") return "danger";
    if (s === "completed" || s === "success") return "success";
    if (s === "running" || s === "in_progress" || s === "processing") return "primary";
    if (s === "created" || s === "queued" || s === "pending" || s === "warning") return "warning";
    return "secondary";
}

function jobToTreeNode(job: Job): TreeNode {
    return {
        id: String(job.id),
        label: job.name || job.type,
        type: job.status,
        icon: <Badge bg={getStatusColor(job.status)} className="job-tree__status-badge">{job.status}</Badge>,
        data: job,
        children: job.children?.map(jobToTreeNode) ?? [],
    };
}

/** Recursively collect all job IDs for default expansion. */
export function collectAllJobIds(job: Job): Set<string> {
    const ids = new Set<string>([String(job.id)]);
    for (const child of job.children ?? []) {
        for (const id of collectAllJobIds(child)) {
            ids.add(id);
        }
    }
    return ids;
}

interface JobTreeSidebarProps {
    rootJob: Job;
    selectedJobId: string;
    expandedIds: Set<string>;
    onSelect: (node: TreeNode) => void;
    onToggle: (nodeId: string) => void;
    onRefresh?: () => void;
}

export default function JobTreeSidebar({ rootJob, selectedJobId, expandedIds, onSelect, onToggle, onRefresh }: JobTreeSidebarProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const treeNodes = useMemo(() => [jobToTreeNode(rootJob)], [rootJob]);

    return (
        <div className="job-tree-sidebar">
            <div className="job-tree-sidebar__header">
                <button
                    type="button"
                    className="job-tree-sidebar__back-btn"
                    onClick={() => {
                        // Back to the list as it was left. `selected` belongs to this
                        // page, not to the list, so it does not travel back.
                        const query = new URLSearchParams(searchParams);
                        query.delete("selected");
                        const suffix = query.toString();
                        navigate(
                            `${import.meta.env.BASE_URL}${Paths.JOBS}${suffix ? `?${suffix}` : ""}`,
                        );
                    }}
                >
                    <i className="bi bi-arrow-left" /> Back to Jobs
                </button>
                {onRefresh && (
                    <button
                        type="button"
                        className="job-tree-sidebar__refresh-btn"
                        onClick={onRefresh}
                        title="Refresh job tree"
                    >
                        <i className="bi bi-arrow-clockwise" />
                    </button>
                )}
            </div>
            <TreeNavigator
                nodes={treeNodes}
                selectedId={selectedJobId}
                onSelect={onSelect}
                expandedIds={expandedIds}
                onToggle={onToggle}
            />
        </div>
    );
}
